import React, {useEffect, useRef, useState} from 'react'
import {Box, Button, CssBaseline, Grid, Typography, Alert, useMediaQuery, useTheme} from "@mui/material";
import CustomNavbar from "../components/CustomNavbar";
import Title from "../components/Title";
import BackButton from "../components/BackButton";
import {useAppDispatch} from "../store/store";
import {resetCalculation, sendEventsData} from "../store/actions/actions";
import Footer from "../components/Footer";
import LeftNavigation from "../components/LeftNavigation";
import DiagramLayout from "../layouts/result/DiagramLayout";
import PictureLayout from "../layouts/result/PictureLayout";
import InputLayout from "../layouts/result/InputLayout";
import TableLayout from "../layouts/result/TableLayout";
import GraphicsLayout from "../layouts/result/GraphicsLayout";
import {useNavigate} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {MAIN_COLOR_BLUE} from "../utils/styles";
import PrintIcon from '@mui/icons-material/Print';
import DownloadIcon from '@mui/icons-material/Download';
import RightLogo from "../components/RightLogo";
import {useReactToPrint} from "react-to-print";
import * as FileSaver from 'file-saver'
import XLSX from 'sheetjs-style'
import {getCurrentLanguage} from "../utils/Helpers";
const tree_species = require("../data/tree_species.json")
const sky_view_factors = require("../data/sky_view_factor.json")
const ResultPage = () => {
    const [langKey, setLangKey] = useState<string>()
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const {t, i18n} = useTranslation()
    const theme = useTheme();
    const smScreen = useMediaQuery(theme.breakpoints.up("sm"));
    const componentRef = useRef<any>(null);
    const promiseResolveRef = useRef<any>(null);
    const [isPrinting, setIsPrinting] = useState(false);
    const input = JSON.parse(localStorage.getItem("tum_leitfaden_input") || '{}') as any
    const tree_specie = tree_species["de"]?.find((tree_specie: any) => tree_specie.id === input?.tree_species)
    const sky_view_factor = sky_view_factors["de"]?.find((factor:any) => factor.id === input?.svf_id)
    
    // Safely parse result from localStorage
    let result: any = null;
    try {
        const resultStr = localStorage.getItem("tum_leitfaden_result");
        if (resultStr && resultStr !== '[]' && resultStr !== 'null') {
            result = JSON.parse(resultStr);
        }
    } catch (e) {
        console.error('Error parsing result data:', e);
        result = null;
    }
    
    // Check if result is valid and not empty
    const hasValidResult = result && Array.isArray(result) && result.length > 0
    
    // Check if input data exists
    const hasValidInput = input && Object.keys(input).length > 0
    
    useEffect(() => {
        dispatch(sendEventsData({
            date: new Date().toLocaleString(),
            timestamp: new Date().getTime(),
            value:"/result",
            type:"pageView"
        }))
    }, []);
    const handlePrint = useReactToPrint({
        content: () => componentRef.current,
        onBeforeGetContent: () => {
            return new Promise((resolve) => {
                promiseResolveRef.current = resolve;
                setIsPrinting(true);
            });
        },
        onAfterPrint: () => {
            promiseResolveRef.current = null;
            setIsPrinting(false);
        },
        documentTitle: 'Print Result Page',
    } as any);


    const retrieveSpecificData = (columnName: string) => {
        if (!hasValidResult) return 0;
        const found = result.find((data: any) => data.name === columnName);
        return found && found.value !== null && found.value !== undefined ? found.value : 0;
    }
    const divideData = (data: any, divider: any) => {
        return (data / divider).toFixed(1).toString().replace(".", ",")
    }
    const inputHeaders = [
        t('result_page.city' as any),
        t('result_page.tree_species' as any),
        t('result_page.soil_type' as any),
        t('result_page.tree_size' as any),
        t('result_page.soil_sealing' as any),
        t('result_page.sky_view_factor' as any),
        t('result_page.period' as any),
    ]
    const inputData = [
        input?.city || '',
        tree_specie ? (tree_specie.name + " (" + tree_specie.scientific_name + ")") : '',
        input?.soil_type || '',
        input?.tree_size || '',
        input?.soil_sealing || '',
        sky_view_factor?.name || '',
        input?.period || ''
    ]
    const outputHeaders = [
        t('period' as any),
        t('crown_diameter' as any) + " [cm]",
        t('tree_height' as any) + " [m]",
        t('crown_projection_area' as any) + " [m"+String.fromCodePoint(0x00B2)+ "]",
        t('crown_projection_increase' as any)+ " [m"+String.fromCodePoint(0x00B2) +"/"+  t('year' as any) +"]",
        t('biomass' as any)+ " [kg C]",
        t('biomass_increase' as any) + " [kg/" +  t('year' as any)+"]",
        t('annual_growth_rate' as any)  +  " [%]",
        t('dryness_index' as any),
        t('water_use_efficiency' as any)+ " [g/kg H"+String.fromCodePoint(0x2082)+"0]",
        'CO'+String.fromCodePoint(0x2082) +"-"+ t('storage' as any)  +" [kg CO" +String.fromCodePoint(0x2082)+ "/" +t('year' as any)+ "]",
        t('water_usage' as any) +" [m"+String.fromCodePoint(0x00B3)+"/"+t('year' as any)+"]",
        t('transpiration' as any) + " [mm]",
        t('interception' as any) + " [mm]",
        t('drain' as any) + " [mm]",
        t('cooling_perspiration' as any)+" [kWh "+t('in' as any) + " " +t('summer' as any) +"]",
        t('cooling_shading' as any) + " [kWh "+t('in' as any)+ " " +t('summer' as any)+"]"
    ]
    const outputData=[
        input?.period || '',
        retrieveSpecificData("dbh").toFixed(1).replace(".",",") + " cm",
        retrieveSpecificData("height").toFixed(1).replace(".",",") +  " m",
        retrieveSpecificData("CPA").toFixed(1).replace(".",",") + " m" +String.fromCodePoint(0x00B2),
        retrieveSpecificData("inc CPA").toFixed(1).replace(".",",")+ " m" +String.fromCodePoint(0x00B2) +"/"+  t('year' as any),
        retrieveSpecificData("Bti").toFixed(0).replace(".",",") + " kg C",
        retrieveSpecificData("inc bio").toFixed(1).replace(".",",") + " kg/" +  t('year' as any),
        retrieveSpecificData("gr%").toFixed(1).replace(".",",") + " %",
        retrieveSpecificData("DI").toFixed(2).replace(".",","),
        divideData(retrieveSpecificData("inc bio") as any, (result?.[63]?.value || 0)) as any + " g/kg H",
        retrieveSpecificData("CO stor").toFixed(0),
        (result?.[66]?.value || 0).toFixed(0),
        (result?.[62]?.value || 0).toFixed(0),
        (result?.[64]?.value || 0).toFixed(0),
        retrieveSpecificData("ro").toFixed(0),
        retrieveSpecificData("cool(tr) su").toFixed(0),
        retrieveSpecificData("cool(sh) su").toFixed(0)
    ]

    const csvData = [
        inputHeaders,
        inputData,
        outputHeaders,
        outputData
    ];

    const exportToExcel = async  () =>{
        const fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8'
        const fileExtension = '.xlsx'
        const ws = XLSX.utils.aoa_to_sheet(csvData)
        const wb = {Sheets: {'data': ws}, SheetNames:['data']}
        const buffer =XLSX.write(wb,{bookType: 'xlsx',type:'array'})
        const data = new Blob([buffer],{type: fileType})
        FileSaver.saveAs(data,"result_"+new Date().toLocaleString() + fileExtension)
    }


    useEffect(() => {
        if (isPrinting && promiseResolveRef.current) {
            promiseResolveRef.current();
        }
    }, [isPrinting]);



    useEffect(() => {
        if(i18n.language){
            setLangKey(getCurrentLanguage(i18n))
        }
    }, [i18n]);


    window.onpopstate = () => {
        navigate("/simulation");
        dispatch(resetCalculation() as any)
    }

    const onClick =()=>{
        navigate("/simulation")
        dispatch(resetCalculation() as any)
    }
    window.onbeforeunload = function() {
        navigate("/simulation")
        dispatch(resetCalculation() as any)
    };
    // Show error message if no result data or missing input
    if (!hasValidResult || !hasValidInput) {
        return(
            <React.Fragment>
                <CssBaseline/>
                <CustomNavbar/>
                <BackButton onClick={onClick}/>
                <Title/>
                <Grid container spacing={3}>
                    <Grid item xs={2}>
                        <LeftNavigation />
                    </Grid>
                    <Grid item xs={8} style={{padding: 4}}>
                        <Alert severity="warning" sx={{marginTop: 4, marginBottom: 4}}>
                            <Typography variant="h6" gutterBottom>
                                {t('result_page.no_data_title' as any) || 'No Results Available'}
                            </Typography>
                            <Typography variant="body1">
                                {t('result_page.no_data_message' as any) || 'The calculation did not return any results. Please try running the simulation again.'}
                            </Typography>
                            <Button
                                variant="contained"
                                sx={{
                                    backgroundColor: MAIN_COLOR_BLUE,
                                    marginTop: 2,
                                    '&:hover': {
                                        background: MAIN_COLOR_BLUE
                                    },
                                }}
                                onClick={onClick}
                            >
                                {t('result_page.back_to_simulation' as any) || 'Back to Simulation'}
                            </Button>
                        </Alert>
                    </Grid>
                    <Grid item xs={2}>
                        <RightLogo/>
                    </Grid>
                </Grid>
                <Footer/>
            </React.Fragment>
        )
    }

    return(
        <React.Fragment>
            <CssBaseline/>
            <CustomNavbar/>
            <BackButton onClick={onClick}/>
            <Title/>
            <Grid container spacing={3}>
                <Grid item xs={2}>
                    <LeftNavigation />
                </Grid>
                <Grid item xs={8} style={{padding: 4}}>
                    <Grid container spacing={6}>
                        <Grid item xs={7}>
                            {t && i18n &&   <InputLayout t={t} i18n={i18n} /> }
                        </Grid>
                        <Grid item xs={5}>
                            {
                                langKey &&  <PictureLayout language={langKey} smScreen={smScreen} />
                            }
                        </Grid>
                    </Grid>
                    <Grid container sx={{marginBottom: 4}} item spacing={2}>
                        <Grid item xs={7}>
                            { t &&  <TableLayout t={t} /> }
                        </Grid>
                        <Grid item xs={5}>
                            {
                                langKey && <DiagramLayout language={langKey} smScreen={smScreen}/>
                            }
                        </Grid>
                    </Grid>
                    <Grid container>
                        <GraphicsLayout t={t} />
                    </Grid>
                    <Grid container>
                        <Grid item sm={12}>
                            <Box display="flex" justifyContent="flex-end">
                                <Button
                                    disableRipple={true}
                                    sx={{
                                        backgroundColor: MAIN_COLOR_BLUE,
                                        color: "white",
                                        fontSize: "14px",
                                        fontWeight: "bold",
                                        marginTop: "16px",
                                        marginRight: "16px",
                                        padding: "10px 10px",
                                        '&:hover': {
                                            background: MAIN_COLOR_BLUE
                                        },
                                    }}
                                    onClick={()=>exportToExcel()}
                                >
                                    <DownloadIcon sx={{mr: "10px"}}/>
                                        {t('result_page.save_values' as any)}
                                </Button>
                                <Button
                                    disableRipple={true}
                                    sx={{
                                        backgroundColor: MAIN_COLOR_BLUE,
                                        color: "white",
                                        fontSize: "14px",
                                        fontWeight: "bold",
                                        marginTop: "16px",
                                        padding: "10px 10px",
                                        '&:hover': {
                                            background: MAIN_COLOR_BLUE
                                        },
                                    }}
                                    onClick={handlePrint}
                                >
                                    <PrintIcon sx={{mr: "10px"}}/>
                                    {t('result_page.print' as any)}
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={2}>
                    <RightLogo/>
                </Grid>
            </Grid>
            <div style={{display: "none"}}>
                <Grid container ref={componentRef}>
                    <Grid item xs={12}>
                        <Grid container>
                            <Grid item xs={7}>
                                {langKey && t &&   <InputLayout langKey={langKey} t={t} /> }
                            </Grid>
                            <Grid item xs={5}>
                                {
                                    langKey &&  <PictureLayout isPrinting={isPrinting} language={langKey} smScreen={smScreen} />
                                }
                            </Grid>
                        </Grid>
                        <Grid container>
                            <Grid item xs={isPrinting? 6 : 8}>
                                { t &&  <TableLayout isPrinting={isPrinting} t={t} /> }
                            </Grid>
                            <Grid item xs={isPrinting? 6 : 4}>
                                {
                                    langKey && <DiagramLayout isPrinting={isPrinting} language={langKey} smScreen={smScreen}/>
                                }
                            </Grid>
                        </Grid>
                        <Grid container>
                            <GraphicsLayout t={t} isPrinting={isPrinting} />
                        </Grid>
                    </Grid>
                </Grid>
            </div>
            <Footer/>
        </React.Fragment>
    )
}
export default ResultPage
